import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ActivateIcon,
  Block as DeactivateIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import {
  normalizeAccount,
  normalizeAccountClass,
  normalizeAccountType,
  normalizeAccountTypes,
  normalizeAccounts,
  normalizeSubType,
} from '../../utils/ledgerIds';
import { useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import {
  AccountClassPicker,
  AccountPicker,
  AccountTypePicker,
  CurrencyPicker,
  SubTypePicker,
  TaxCodePicker,
} from './LedgerPickers';
import ModulePageHeader from '../common/ModulePageHeader';
import LedgerListPage from './LedgerListPage';
import LedgerFormDialog from './LedgerFormDialog';

const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

// LedgerListPage expects the gateway envelope; stamp canonical `.id` on lists
// so every handler below only touches `item.id`.
const asListResponse = (res, normalize) => {
  const outer = res?.data ?? {};
  const payload = outer.data ?? outer;
  const rawList = Array.isArray(payload) ? payload : payload.items ?? [];
  const data = Array.isArray(payload)
    ? { items: normalize(rawList) }
    : { ...payload, items: normalize(rawList) };
  return { ...res, data: { ...outer, data } };
};

const activeChip = (value) => (
  <Chip label={value ? 'Active' : 'Inactive'} color={value ? 'success' : 'default'} size="small" />
);

const BALANCE_TYPES = ['debit', 'credit'];
const STATEMENT_TYPES = ['balance_sheet', 'income_statement'];
const REPORTING_CATEGORIES = ['asset', 'liability', 'equity', 'income', 'expense', 'off_balance_sheet'];
const CASH_FLOW_TYPES = ['operating', 'investing', 'financing', 'not_applicable'];
const SHEET_SECTIONS = [
  'CURRENT_ASSETS', 'FIXED_ASSETS', 'OTHER_ASSETS', 'CURRENT_LIABILITIES',
  'LONG_TERM_LIABILITIES', 'OTHER_LIABILITIES', 'EQUITY',
];
const ACCOUNT_STATUSES = ['ACTIVE', 'INACTIVE'];

const CLASS_FIELDS = [
  { key: 'class_name', label: 'Class Name', required: true, helper: 'Unique, e.g. Assets' },
  { key: 'code_start', label: 'Code Start', type: 'number', required: true, min: 1000 },
  { key: 'code_end', label: 'Code End', type: 'number', required: true, min: 1000 },
  {
    key: 'balance_type', label: 'Balance Type', type: 'select', required: true,
    options: BALANCE_TYPES.map((v) => ({ label: v, value: v })),
  },
  {
    key: 'financial_statement_type', label: 'Statement', type: 'select', required: true,
    options: STATEMENT_TYPES.map((v) => ({ label: v, value: v })),
  },
  {
    key: 'reporting_category', label: 'Reporting Category', type: 'select', required: true,
    options: REPORTING_CATEGORIES.map((v) => ({ label: v, value: v })),
  },
  {
    key: 'cash_flow_type', label: 'Cash Flow Type', type: 'select', required: true,
    options: CASH_FLOW_TYPES.map((v) => ({ label: v, value: v })),
  },
];

const TYPE_FIELDS = [
  { key: 'type_name', label: 'Type Name', required: true },
  { key: 'account_class_id', label: 'Class', type: 'picker', picker: AccountClassPicker, required: true, helper: 'Chart class owning the code fence' },
  { key: 'code', label: 'Code', helper: 'Blank to auto-generate' },
  { key: 'description', label: 'Description', type: 'textarea', md: 12 },
  { key: 'suggested_range_start', label: 'Range Start', type: 'number', min: 1000 },
  { key: 'suggested_range_end', label: 'Range End', type: 'number', min: 1000 },
  {
    key: 'balance_sheet_section', label: 'Balance Sheet Section', type: 'select',
    options: [{ label: '(none)', value: '' }, ...SHEET_SECTIONS.map((v) => ({ label: v, value: v }))],
  },
  { key: 'display_order', label: 'Display Order', type: 'number' },
  { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
];

const SUBTYPE_FIELDS = [
  { key: 'sub_type_name', label: 'Sub-Type Name', required: true },
  { key: 'account_type_id', label: 'Type', type: 'picker', picker: AccountTypePicker, required: true, helper: 'Parent type dividing the class fence' },
  { key: 'code', label: 'Code', helper: 'Blank to auto-generate' },
  { key: 'description', label: 'Description', type: 'textarea', md: 12 },
  { key: 'suggested_range_start', label: 'Range Start', type: 'number', min: 1000 },
  { key: 'suggested_range_end', label: 'Range End', type: 'number', min: 1000 },
  { key: 'is_control_account', label: 'Control account (forces recon + no posting)', type: 'switch', md: 12 },
  { key: 'requires_reconciliation', label: 'Requires reconciliation', type: 'switch', md: 12 },
  { key: 'is_posting_allowed', label: 'Posting allowed', type: 'switch', md: 12 },
  { key: 'internal_code', label: 'Internal Code' },
  { key: 'display_order', label: 'Display Order', type: 'number' },
  { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
];

const ACCOUNT_FIELDS = [
  { key: 'account_code', label: 'Account Code', helper: 'Blank to auto-generate from sub-type slot' },
  { key: 'account_name', label: 'Account Name', required: true },
  { key: 'sub_type_id', label: 'Sub-Type', type: 'picker', picker: SubTypePicker, helper: 'Sub-type implying the code range (one of sub-type / type is required)' },
  { key: 'account_type_id', label: 'Type (alt)', type: 'picker', picker: AccountTypePicker, helper: 'Alternative when creating directly under a type' },
  { key: 'description', label: 'Description', type: 'textarea', md: 12 },
  {
    key: 'status', label: 'Status', type: 'select',
    options: ACCOUNT_STATUSES.map((v) => ({ label: v, value: v })),
  },
  { key: 'is_header', label: 'Header account (holds no postings)', type: 'switch', md: 12 },
  { key: 'allow_posting', label: 'Allow posting', type: 'switch', md: 12 },
  { key: 'requires_reconciliation', label: 'Requires reconciliation', type: 'switch', md: 12 },
  { key: 'is_cash_equivalent', label: 'Cash equivalent (IAS 7)', type: 'switch', md: 12 },
  { key: 'currency_id', label: 'Currency', type: 'picker', picker: CurrencyPicker },
  { key: 'tax_code_id', label: 'Tax Code', type: 'picker', picker: TaxCodePicker },
  { key: 'opening_balance', label: 'Opening Balance', type: 'number', helper: 'Rejected for header/control/income-statement accounts' },
  { key: 'opening_balance_date', label: 'Opening Balance Date', type: 'date' },
  { key: 'is_range_override', label: 'Range override (code outside suggested range)', type: 'switch', md: 12 },
  { key: 'override_reason', label: 'Override Reason', type: 'textarea', md: 12 },
  { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
];

// ── Filter option helpers ─────────────────────────────────────────────────
const toNum = (v) => (v === '' || v === undefined || v === null ? undefined : Number(v));
const boolOpts = (allLabel = 'All') => [
  { label: allLabel, value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const enumOpts = (values, allLabel = 'All') => [
  { label: allLabel, value: '' },
  ...values.map((v) => ({ label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value: v })),
];

// Accounts tab filters — AccountQueryDto
const ACCOUNT_STATUS_OPTS = enumOpts(['ACTIVE', 'INACTIVE', 'CLOSED', 'SUSPENDED'], 'All statuses');

// Classes tab filters — AccountClassQueryDto
const REPORTING_CATEGORY_OPTS = enumOpts(
  ['asset', 'liability', 'equity', 'income', 'expense', 'off_balance_sheet'], 'All categories'
);
const FINANCIAL_STATEMENT_OPTS = enumOpts(['balance_sheet', 'income_statement'], 'All statements');
const BALANCE_TYPE_OPTS = enumOpts(['debit', 'credit'], 'All balance types');

// Types tab filters — AccountTypeQueryDto
const BALANCE_SHEET_SECTION_OPTS = enumOpts(
  ['CURRENT_ASSETS', 'FIXED_ASSETS', 'OTHER_ASSETS', 'CURRENT_LIABILITIES',
   'LONG_TERM_LIABILITIES', 'OTHER_LIABILITIES', 'EQUITY'], 'All sections'
);

const EMPTY_VALUES = {
  account_code: '', account_name: '', description: '', status: 'ACTIVE',
  is_header: false, allow_posting: true, requires_reconciliation: false,
  is_cash_equivalent: false, currency_id: '', parent_id: '',
  opening_balance: '', opening_balance_date: '',
  is_range_override: false, override_reason: '', notes: '',
};

// Single dialog for create (2-step stepper), edit, and child-add.
// mode: 'create' | 'edit' | 'child'
// edit/child skip step 1 and go straight to the details form.
const AccountDialog = ({ open, onClose, onSubmit, saving, mode, editing, parentAccount }) => {
  const isCreate = mode === 'create';
  const isEdit   = mode === 'edit';
  const isChild  = mode === 'child';

  const [step, setStep] = React.useState(isCreate ? 1 : 2);
  const [accountTypes, setAccountTypes] = React.useState([]);
  const [selectedType, setSelectedType] = React.useState(null);
  const [selectedSubTypeId, setSelectedSubTypeId] = React.useState('');
  const [values, setValues] = React.useState(EMPTY_VALUES);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setError(null);

    if (isCreate) {
      setStep(1);
      setSelectedType(null);
      setSelectedSubTypeId('');
      setValues(EMPTY_VALUES);
      ledgerAPI.getAccountTypes({ limit: 100 })
        .then((res) => {
          const payload = res?.data?.data ?? res?.data ?? {};
          setAccountTypes(Array.isArray(payload) ? payload : payload.items ?? []);
        })
        .catch(() => {});
    } else if (isEdit && editing) {
      setStep(2);
      setSelectedType(null); // type shown as read-only badge from editing data
      setValues({
        account_code:           editing.account_code || '',
        account_name:           editing.account_name || '',
        description:            editing.description || '',
        status:                 editing.status || 'ACTIVE',
        is_header:              Boolean(editing.is_header),
        allow_posting:          editing.allow_posting !== false,
        requires_reconciliation:Boolean(editing.requires_reconciliation),
        is_cash_equivalent:     Boolean(editing.is_cash_equivalent),
        currency_id:            editing.currency_id ?? '',
        parent_id:              editing.parent_id ?? editing.parent?.account_id ?? '',
        opening_balance:        editing.opening_balance ?? '',
        opening_balance_date:   editing.opening_balance_date
                                  ? String(editing.opening_balance_date).slice(0, 10) : '',
        is_range_override:      Boolean(editing.is_range_override),
        override_reason:        editing.override_reason || '',
        notes:                  editing.notes || '',
      });
    } else if (isChild) {
      setStep(2);
      setSelectedType(null);
      setValues(EMPTY_VALUES);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = React.useMemo(() => {
    const g = {};
    accountTypes.forEach((t) => {
      const cls = t.accountClass?.class_name ?? t.class_name ?? 'Other';
      if (!g[cls]) g[cls] = [];
      g[cls].push(t);
    });
    return g;
  }, [accountTypes]);

  // Determine whether to show the opening-balance section.
  // For create: driven by the selected type.
  // For edit/child: driven by the account's own type data (stored on editing).
  const editingStatementType =
    editing?.sub_type?.accountType?.accountClass?.financial_statement_type ??
    editing?.accountType?.accountClass?.financial_statement_type ??
    null;
  const isBalanceSheet =
    selectedType?.accountClass?.financial_statement_type === 'balance_sheet' ||
    selectedType?.financial_statement_type === 'balance_sheet' ||
    editingStatementType === 'balance_sheet';

  const set = (key, val) => setValues((p) => ({ ...p, [key]: val }));

  const buildPayload = () => {
    const clean = { ...values };
    Object.keys(clean).forEach((k) => { if (clean[k] === '' || clean[k] === undefined) delete clean[k]; });
    if (clean.currency_id !== undefined) clean.currency_id = Number(clean.currency_id);
    if (clean.opening_balance !== undefined) clean.opening_balance = Number(clean.opening_balance);
    if (isCreate && selectedType) {
      const typeId = selectedType.account_type_id ?? selectedType.id;
      if (typeId) clean.account_type_id = Number(typeId);
      // Explicit sub-type choice takes priority over the type shortcut —
      // the ledger otherwise resolves an arbitrary sub-type under the type.
      if (selectedSubTypeId) clean.sub_type_id = Number(selectedSubTypeId);
    }
    if (isCreate && clean.parent_id !== undefined) clean.parent_id = Number(clean.parent_id);
    if (isEdit) {
      // UpdateAccountDto has no parent_id field at all (forbidNonWhitelisted
      // would 400) — a parent change on edit goes through the dedicated
      // reparent endpoint instead; see submitAccount's follow-up call.
      delete clean.parent_id;
    }
    if (isChild) {
      // CreateChildAccountDto (accounting-apis) only knows this fixed field
      // set and rejects anything else (forbidNonWhitelisted) — it always
      // inherits sub_type_id from the parent server-side, so none of the
      // type/sub-type/range-override/parent fields belong here.
      delete clean.is_range_override;
      delete clean.override_reason;
      delete clean.account_type_id;
      delete clean.sub_type_id;
      delete clean.parent_id;
    }
    return clean;
  };

  const handleSubmit = () => {
    const name = values.account_name?.trim() || '';
    if (!name) { setError('Account name is required'); return; }
    if (name.length < 2) { setError('Account name must be at least 2 characters'); return; }
    if (name.length > 200) { setError('Account name cannot exceed 200 characters'); return; }
    if (values.account_code && !/^\d{4,}$/.test(values.account_code.trim())) {
      setError('Account code must be a numeric string of at least 4 digits (leave blank to auto-generate)');
      return;
    }
    if (values.is_range_override && !values.override_reason?.trim()) {
      setError('Override reason is required when placing the code outside its suggested range');
      return;
    }
    setError(null);
    if (isEdit) {
      const originalParentId = editing?.parent_id ?? editing?.parent?.account_id ?? '';
      const parentChanged = String(values.parent_id || '') !== String(originalParentId || '');
      const newParentId = values.parent_id === '' ? null : Number(values.parent_id);
      onSubmit(buildPayload(), { reparentTo: parentChanged ? newParentId : undefined });
      return;
    }
    onSubmit(buildPayload());
  };

  // Title and subtitle vary by mode
  const dialogTitle = isEdit
    ? `Edit Account${editing?.account_code ? ` · ${editing.account_code}` : ''}`
    : isChild
      ? `New Child of ${parentAccount?.account_code || parentAccount?.account_name || ''}`
      : 'Add Account';

  const dialogSubtitle = isCreate
    ? (step === 1 ? 'Select the account type' : 'Fill in the account details')
    : isEdit
      ? 'Update account details'
      : `Child account under ${parentAccount?.account_name || ''}`;

  // Context badge shown on step 2 for all modes
  const contextBadge = isCreate && selectedType ? (
    <Alert severity="info" sx={{ py: 0.5 }}>
      Creating under <strong>{selectedType.accountClass?.class_name ?? ''}</strong> — <strong>{selectedType.type_name}</strong>
    </Alert>
  ) : isEdit && editing ? (
    <Alert severity="info" sx={{ py: 0.5 }}>
      Editing <strong>{editing.account_code}</strong>
      {editing.sub_type?.sub_type_name ? ` · ${editing.sub_type.sub_type_name}` : ''}
    </Alert>
  ) : isChild && parentAccount ? (
    <Alert severity="info" sx={{ py: 0.5 }}>
      Child of <strong>{parentAccount.account_code}</strong> — {parentAccount.account_name}
    </Alert>
  ) : null;

  // Parent account is fixed for child-add (parentAccount prop) — only
  // create/edit let you pick or clear one here. Scoped to the same account
  // type and active accounts only, matching what the ledger's reparent
  // endpoint actually accepts (ReparentAccountDto + AccountService.reparent).
  const parentPickerTypeId = isCreate
    ? (selectedType?.account_type_id ?? selectedType?.id)
    : (editing?.sub_type?.accountType?.account_type_id ?? editing?.accountType?.account_type_id);

  // The details form — identical for all three modes
  const detailsForm = (
    <Stack spacing={2}>
      {contextBadge}
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField
          label="Account Code" size="small"
          value={values.account_code}
          onChange={(e) => set('account_code', e.target.value)}
          inputProps={{ maxLength: 20 }}
          helperText={
            isEdit
              ? 'Rejected once this account has posted journal entries'
              : 'Blank to auto-generate. Numeric, at least 4 digits.'
          }
        />
        <TextField
          label="Account Name" size="small" required
          value={values.account_name}
          onChange={(e) => set('account_name', e.target.value)}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label="Description" size="small" multiline rows={2}
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          inputProps={{ maxLength: 1000 }}
          sx={{ gridColumn: '1 / -1' }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={values.status} onChange={(e) => set('status', e.target.value)}>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="CLOSED">Closed</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
          </Select>
        </FormControl>
        <CurrencyPicker
          label="Currency (optional)"
          value={values.currency_id}
          onChange={(id) => set('currency_id', id)}
          helper={isEdit ? 'Rejected once this account has posted journal entries' : undefined}
        />
        {!isChild && (
          <AccountPicker
            label="Parent Account (optional — clear for top-level)"
            value={values.parent_id}
            onChange={(id) => set('parent_id', id)}
            typeId={parentPickerTypeId}
            activeOnly
            exclude={isEdit ? editing?.id : undefined}
            helper="Only active accounts of the same type are shown."
            sx={{ gridColumn: '1 / -1' }}
          />
        )}
      </Box>

      {isBalanceSheet && (
        <Box>
          <Divider sx={{ my: 1 }}><Chip label="Opening Balance" size="small" /></Divider>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <TextField
              label="Opening Balance" size="small" type="number"
              value={values.opening_balance}
              onChange={(e) => set('opening_balance', e.target.value)}
              inputProps={{ step: '0.01' }}
              helperText="Leave blank for zero"
            />
            <TextField
              label="As of Date" size="small" type="date"
              value={values.opening_balance_date}
              onChange={(e) => set('opening_balance_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional — defaults to the opening period's start date"
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 0.5 }}><Chip label="Flags" size="small" /></Divider>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={Boolean(values.is_header)}
              onChange={(e) => {
                const isHeader = e.target.checked;
                // A header account can never post — mirror the ledger's hard
                // rule client-side instead of letting both switches sit on
                // and only failing after Save.
                setValues((p) => ({ ...p, is_header: isHeader, allow_posting: isHeader ? false : p.allow_posting }));
              }}
            />
          }
          label={<Typography variant="body2">Header (no postings)</Typography>}
        />
        <Tooltip title={values.is_header ? 'Header accounts cannot allow posting' : ''} placement="top" disableHoverListener={!values.is_header}>
          <span>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(values.allow_posting)}
                  disabled={Boolean(values.is_header)}
                  onChange={(e) => set('allow_posting', e.target.checked)}
                />
              }
              label={<Typography variant="body2">Allow posting</Typography>}
            />
          </span>
        </Tooltip>
        {[
          { key: 'requires_reconciliation', label: 'Requires reconciliation' },
          { key: 'is_cash_equivalent',      label: 'Cash equivalent (IAS 7)' },
        ].map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={<Switch size="small" checked={Boolean(values[key])} onChange={(e) => set(key, e.target.checked)} />}
            label={<Typography variant="body2">{label}</Typography>}
          />
        ))}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={Boolean(values.is_range_override)}
              onChange={(e) => set('is_range_override', e.target.checked)}
            />
          }
          label={<Typography variant="body2">Code outside suggested range</Typography>}
        />
      </Box>

      {values.is_range_override && (
        <TextField
          label="Override Reason" size="small" required multiline rows={2}
          value={values.override_reason}
          onChange={(e) => set('override_reason', e.target.value)}
          inputProps={{ maxLength: 1000 }}
          helperText="Required when the code is placed outside its sub-type's suggested range"
        />
      )}

      <TextField
        label="Notes" size="small" multiline rows={2}
        value={values.notes}
        onChange={(e) => set('notes', e.target.value)}
        inputProps={{ maxLength: 1000 }}
      />
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>{dialogTitle}</Typography>
        <Typography variant="body2" color="text.secondary">{dialogSubtitle}</Typography>
      </DialogTitle>

      {isCreate && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Stepper activeStep={step - 1}>
            <Step><StepLabel>Account Type</StepLabel></Step>
            <Step><StepLabel>Details</StepLabel></Step>
          </Stepper>
        </Box>
      )}

      <DialogContent sx={{ pt: 2 }}>
        {isCreate && step === 1 ? (
          <Box>
            {Object.entries(grouped).map(([cls, types], ci) => (
              <Box key={cls} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 600, mb: 1, color: 'text.secondary',
                  textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em',
                }}>
                  {cls}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {types.map((t) => {
                    const tid  = t.account_type_id ?? t.id;
                    const stid = selectedType?.account_type_id ?? selectedType?.id;
                    const sel  = String(tid) === String(stid);
                    return (
                      <Card key={tid} variant="outlined" onClick={() => { setSelectedType(t); setSelectedSubTypeId(''); }} sx={{
                        cursor: 'pointer',
                        borderColor: sel ? 'primary.main' : 'divider',
                        bgcolor: sel ? 'primary.50' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      }}>
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant="body2" fontWeight={600}>{t.type_name}</Typography>
                          {t.description && (
                            <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                          )}
                          {(t.suggested_range_start || t.code) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t.code ? `Code: ${t.code}` : ''}
                              {t.suggested_range_start ? ` · Range: ${t.suggested_range_start}–${t.suggested_range_end}` : ''}
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
                {ci < Object.keys(grouped).length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
            {selectedType && (
              <Box sx={{ mt: 1, maxWidth: 420 }}>
                <SubTypePicker
                  label="Sub-Type (optional)"
                  helper="Narrows to a specific code range & control-account defaults. Leave blank and the ledger picks one under this type for you."
                  typeId={selectedType.account_type_id ?? selectedType.id}
                  value={selectedSubTypeId}
                  onChange={(id) => setSelectedSubTypeId(id)}
                />
              </Box>
            )}
          </Box>
        ) : detailsForm}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {isCreate && step === 1 ? (
          <Button variant="contained" disabled={!selectedType} onClick={() => setStep(2)}>Next</Button>
        ) : (
          <>
            {isCreate && <Button onClick={() => setStep(1)} disabled={saving}>Back</Button>}
            <Button
              variant="contained" onClick={handleSubmit} disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : isChild ? 'Create Child' : 'Create Account'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const fmt = (v) => v != null && v !== '' ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

// Row-level actions dropdown — words not icons
const RowActionsMenu = ({ account, canCreate, canUpdate, canDelete, onEdit, onAddChild, onReparent, onActivate, onDeactivate, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const isActive = account.status === 'ACTIVE';
  const children = account._children || [];
  // Mirror the ledger's own guards (AccountService.deactivate/delete) so
  // these menu items explain themselves instead of silently 400-ing.
  const hasActiveChildren = children.some((c) => c.status === 'ACTIVE');
  const deactivateBlockedReason = hasActiveChildren ? 'Deactivate its child accounts first' : null;
  const deleteBlockedReason = account.is_system
    ? 'System-defined accounts cannot be deleted'
    : account.is_control
      ? 'Control accounts cannot be deleted'
      : children.length > 0
        ? 'Delete or reparent its child accounts first'
        : null;
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 2, sx: { minWidth: 170 } }}
        onClick={() => setAnchor(null)}
      >
        {canUpdate && <MenuItem onClick={() => onEdit(account)}>Edit</MenuItem>}
        {canCreate && <MenuItem onClick={() => onAddChild(account)}>Add Child Account</MenuItem>}
        {canUpdate && <MenuItem onClick={() => onReparent(account)}>Move (Reparent)</MenuItem>}
        <Divider />
        {canUpdate && isActive && (
          <Tooltip title={deactivateBlockedReason || ''} placement="left" disableHoverListener={!deactivateBlockedReason}>
            <span>
              <MenuItem
                disabled={Boolean(deactivateBlockedReason)}
                onClick={() => onDeactivate(account)}
                sx={{ color: 'warning.main' }}
              >
                Deactivate
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {canUpdate && !isActive && <MenuItem onClick={() => onActivate(account)}  sx={{ color: 'success.main' }}>Activate</MenuItem>}
        {canDelete && [
          <Divider key="d2" />,
          <Tooltip key="del-tip" title={deleteBlockedReason || ''} placement="left" disableHoverListener={!deleteBlockedReason}>
            <span>
              <MenuItem
                disabled={Boolean(deleteBlockedReason)}
                onClick={() => onDelete(account)}
                sx={{ color: 'error.main' }}
              >
                Delete
              </MenuItem>
            </span>
          </Tooltip>,
        ]}
      </Menu>
    </>
  );
};

// Build a flat list into a tree: roots first, children indented under their parent.
// Returns [{...account, _depth, _hasChildren, _parentCode}]
// balanceByAccountId: account_id -> current closing balance (latest posted
// period), from account_balance — NOT the static opening_balance field,
// which goes stale the moment any transaction posts after account creation.
const buildTree = (flat, balanceByAccountId = {}) => {
  const byId = {};
  flat.forEach((a) => { byId[a.id] = { ...a, _children: [] }; });
  const roots = [];
  flat.forEach((a) => {
    const parentId = a.parent_id ?? a.parent?.account_id ?? null;
    if (parentId && byId[parentId]) byId[parentId]._children.push(byId[a.id]);
    else roots.push(byId[a.id]);
  });

  // Post-order: total balance = own current balance + every descendant's.
  const computeTotalBalance = (node) => {
    const own = Number(balanceByAccountId[node.id]) || 0;
    node._currentBalance = own;
    const childrenTotal = node._children.reduce((sum, c) => sum + computeTotalBalance(c), 0);
    node._totalBalance = own + childrenTotal;
    return node._totalBalance;
  };
  roots.forEach(computeTotalBalance);

  const result = [];
  const walk = (node, depth) => {
    result.push({ ...node, _depth: depth, _hasChildren: node._children.length > 0 });
    node._children.forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return result;
};

const STATUS_COLOR = { ACTIVE: 'success', INACTIVE: 'default', CLOSED: 'error', SUSPENDED: 'warning' };

// Customizable columns for the accounts tree table. Code (tree indent/expand)
// and the row-actions column are structural and always shown; everything
// else can be toggled. Defaults to a lean set; the rest opt-in via "Columns".
const OPTIONAL_COLUMNS = [
  { key: 'name',           label: 'Name' },
  { key: 'class',          label: 'Class' },
  { key: 'type',           label: 'Type' },
  { key: 'subType',        label: 'Sub-Type' },
  { key: 'parent',         label: 'Parent' },
  { key: 'currency',       label: 'Currency' },
  { key: 'openingBalance', label: 'Opening Balance' },
  { key: 'totalBalance',   label: 'Total Balance' },
  { key: 'openingDate',    label: 'Opening Date' },
  { key: 'status',         label: 'Status' },
  { key: 'posting',        label: 'Posting' },
  { key: 'flags',          label: 'Flags' },
];
const DEFAULT_ON_COLUMNS = new Set(['name', 'class', 'type', 'openingBalance', 'totalBalance']);
const DEFAULT_VISIBLE_COLUMNS = OPTIONAL_COLUMNS.reduce((acc, c) => { acc[c.key] = DEFAULT_ON_COLUMNS.has(c.key); return acc; }, {});
const COLUMNS_STORAGE_KEY = 'schoolsync_coa_accounts_columns_v3';

const loadVisibleColumns = () => {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE_COLUMNS };
    return { ...DEFAULT_VISIBLE_COLUMNS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_VISIBLE_COLUMNS };
  }
};

const AccountsTreeTable = ({ refreshKey, canCreate, canUpdate, canDelete, onEdit, onAddChild, onReparent, onActivate, onDeactivate, onDelete }) => {
  const [allAccounts, setAllAccounts] = useState([]);
  const [balanceByAccountId, setBalanceByAccountId] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const PAGE_SIZE = 50;

  useEffect(() => {
    try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns)); } catch { /* private mode / quota — ignore */ }
  }, [visibleColumns]);

  const toggleColumn = (key) => setVisibleColumns((p) => ({ ...p, [key]: !p[key] }));
  const resetColumns = () => setVisibleColumns({ ...DEFAULT_VISIBLE_COLUMNS });

  const FILTERS = [
    { key: 'status',             label: 'Status',      options: ACCOUNT_STATUS_OPTS },
    { key: 'is_header',          label: 'Header',      options: boolOpts('All') },
    { key: 'allow_posting',      label: 'Posting',     options: boolOpts('All') },
    { key: 'is_cash_equivalent', label: 'Cash Equiv.', options: boolOpts('All') },
    { key: 'is_control',         label: 'Control',     options: boolOpts('All') },
    { key: 'is_system',          label: 'System',      options: boolOpts('All') },
  ];

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const baseParams = { limit: 100 };
      if (search) baseParams.search = search;
      FILTERS.forEach((f) => { if (filters[f.key]) baseParams[f.key] = filters[f.key]; });

      // Fetch all pages to build the full tree
      let allItems = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const res = await ledgerAPI.getAccounts({ ...baseParams, page: currentPage });
        const outer = res?.data ?? {};
        const inner = outer?.data !== undefined ? outer.data : outer;
        const rawList = Array.isArray(inner) ? inner : (inner?.items ?? []);
        allItems = allItems.concat(rawList);
        const total = inner?.total ?? rawList.length;
        totalPages = Math.ceil(total / 100);
        currentPage++;
      } while (currentPage <= totalPages && totalPages > 1);

      setAllAccounts(normalizeAccounts(allItems));
      setExpanded(new Set());
      setPage(1);
    } catch (e) {
      console.error('AccountsTreeTable load error', e);
      setLoadError(e?.response?.data?.message || e?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [search, JSON.stringify(filters), refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // account-balances is whitelisted under the 'coa' RBAC section too (same
  // resource this screen already checks), so this needs no extra
  // permission. No "current period" lookup either: fetch every balance row
  // and keep, per account, the one from the latest period by end_date —
  // closing balances carry forward, so that row IS the current balance.
  const loadBalances = React.useCallback(async () => {
    try {
      let allRows = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const res = await ledgerAPI.get('coa', 'account-balances', { limit: 200, page: currentPage });
        const outer = res?.data ?? {};
        const inner = outer?.data !== undefined ? outer.data : outer;
        const rawList = Array.isArray(inner) ? inner : (inner?.items ?? []);
        allRows = allRows.concat(rawList);
        const total = inner?.total ?? rawList.length;
        totalPages = Math.ceil(total / 200);
        currentPage++;
      } while (currentPage <= totalPages && totalPages > 1);

      const latest = {};
      allRows.forEach((row) => {
        const accountId = row.account_id ?? row.account?.account_id;
        if (!accountId) return;
        const endDate = row.period?.end_date ? new Date(row.period.end_date).getTime() : 0;
        const periodNumber = row.period?.period_number ?? 0;
        const prev = latest[accountId];
        const isNewer = !prev || endDate > prev._endDate || (endDate === prev._endDate && periodNumber > prev._periodNumber);
        if (isNewer) {
          latest[accountId] = { balance: Number(row.closing_balance) || 0, _endDate: endDate, _periodNumber: periodNumber };
        }
      });
      const map = {};
      Object.keys(latest).forEach((id) => { map[id] = latest[id].balance; });
      setBalanceByAccountId(map);
    } catch {
      // Balances not initialized yet for this tenant, or no permission —
      // fall back to zero rather than blocking the accounts table on it.
      setBalanceByAccountId({});
    }
  }, [refreshKey]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  const tree = React.useMemo(() => buildTree(allAccounts, balanceByAccountId), [allAccounts, balanceByAccountId]);

  // When searching/filtering, expand all so matches are visible
  const hasFilter = search || Object.values(filters).some(Boolean);
  const treeById = React.useMemo(() => Object.fromEntries(tree.map((a) => [a.id, a])), [tree]);

  const visibleTree = hasFilter ? tree : tree.filter((a) => {
    if (a._depth === 0) return true;
    let cur = a;
    while (cur._depth > 0) {
      const parentId = cur.parent_id ?? cur.parent?.account_id ?? null;
      if (!parentId || !expanded.has(parentId)) return false;
      cur = treeById[parentId];
      if (!cur) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(visibleTree.length / PAGE_SIZE));
  const pageRows = visibleTree.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const clearFilters = () => { setSearch(''); setFilters({}); };
  const hasActiveFilter = search || Object.values(filters).some(Boolean);

  return (
    <Box>
      {/* Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small" placeholder="Search code or name…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flex: '1 1 200px', minWidth: 160 }}
            />
            {FILTERS.map((f) => (
              <TextField
                key={f.key} select size="small" label={f.label}
                value={filters[f.key] || ''}
                onChange={(e) => { setFilters((p) => ({ ...p, [f.key]: e.target.value })); setPage(1); }}
                sx={{ width: 130 }}
              >
                {f.options.map((o) => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            ))}
            {hasActiveFilter && (
              <Button size="small" variant="outlined" color="inherit" onClick={clearFilters} sx={{ whiteSpace: 'nowrap' }}>Clear filters</Button>
            )}
            <Button
              size="small" variant="outlined" color="inherit"
              startIcon={<ViewColumnIcon fontSize="small" />}
              onClick={(e) => setColumnsAnchor(e.currentTarget)}
              sx={{ whiteSpace: 'nowrap', ml: 'auto' }}
            >
              Columns
            </Button>
            <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
              {OPTIONAL_COLUMNS.map((c) => (
                <MenuItem key={c.key} dense onClick={() => toggleColumn(c.key)}>
                  <Checkbox size="small" checked={Boolean(visibleColumns[c.key])} sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={c.label} />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem dense onClick={resetColumns}>
                <ListItemText primary="Reset to default" />
              </MenuItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      {/* Tree table */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box py={6} textAlign="center"><CircularProgress size={28} /></Box>
          ) : loadError ? (
            <Box py={4} px={2}><Alert severity="error">{loadError}</Alert></Box>
          ) : allAccounts.length === 0 ? (
            <Box py={6} textAlign="center"><Typography color="text.secondary">No accounts found.</Typography></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Code</TableCell>
                    {visibleColumns.name && <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Name</TableCell>}
                    {visibleColumns.class && <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>}
                    {visibleColumns.type && <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>}
                    {visibleColumns.subType && <TableCell sx={{ fontWeight: 700 }}>Sub-Type</TableCell>}
                    {visibleColumns.parent && <TableCell sx={{ fontWeight: 700 }}>Parent</TableCell>}
                    {visibleColumns.currency && <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>}
                    {visibleColumns.openingBalance && <TableCell sx={{ fontWeight: 700 }} align="right">Opening Balance</TableCell>}
                    {visibleColumns.totalBalance && (
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        <Tooltip title="Current balance (from posted ledger activity, latest closed period) plus its children's, rolled up recursively" placement="top">
                          <span>Total Balance</span>
                        </Tooltip>
                      </TableCell>
                    )}
                    {visibleColumns.openingDate && <TableCell sx={{ fontWeight: 700 }}>Opening Date</TableCell>}
                    {visibleColumns.status && <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>}
                    {visibleColumns.posting && <TableCell sx={{ fontWeight: 700 }}>Posting</TableCell>}
                    {visibleColumns.flags && <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>}
                    <TableCell sx={{ fontWeight: 700, width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((a, idx) => {
                    const hasKids = a._hasChildren;
                    const isOpen = expanded.has(a.id);
                    return (
                      <TableRow key={a.id} hover sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'action.hover' }}>
                        {/* Code + tree indent + expand toggle */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: a._depth * 2.5 }}>
                            {hasKids ? (
                              <IconButton size="small" sx={{ p: 0.25, mr: 0.5 }} onClick={() => toggle(a.id)}>
                                {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 24, mr: 0.5 }} />
                            )}
                            <Typography variant="body2" fontWeight={a._depth === 0 ? 700 : 400} sx={{ fontFamily: 'monospace' }}>
                              {a.account_code}
                            </Typography>
                          </Box>
                        </TableCell>
                        {visibleColumns.name && (
                          <TableCell>
                            <Tooltip title={a.description || ''} placement="top" disableHoverListener={!a.description}>
                              <Typography variant="body2" fontWeight={a._depth === 0 ? 600 : 400}>{a.account_name}</Typography>
                            </Tooltip>
                          </TableCell>
                        )}
                        {visibleColumns.class && (
                          <TableCell><Typography variant="caption" color="text.secondary">{a.sub_type?.accountType?.accountClass?.class_name || '-'}</Typography></TableCell>
                        )}
                        {visibleColumns.type && (
                          <TableCell><Typography variant="caption">{a.sub_type?.accountType?.type_name || '-'}</Typography></TableCell>
                        )}
                        {visibleColumns.subType && (
                          <TableCell><Typography variant="caption">{a.sub_type?.sub_type_name || '-'}</Typography></TableCell>
                        )}
                        {visibleColumns.parent && (
                          <TableCell>
                            {a.parent ? (
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{a.parent.account_code}</Typography>
                            ) : '-'}
                          </TableCell>
                        )}
                        {visibleColumns.currency && (
                          <TableCell><Typography variant="caption">{a.currency?.currency_code || '-'}</Typography></TableCell>
                        )}
                        {visibleColumns.openingBalance && (
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(a.opening_balance)}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.totalBalance && (
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={hasKids ? 700 : 400} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(a._totalBalance)}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.openingDate && (
                          <TableCell>
                            <Typography variant="caption">
                              {a.opening_balance_date ? String(a.opening_balance_date).slice(0, 10) : '-'}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.status && (
                          <TableCell>
                            <Chip label={a.status || 'ACTIVE'} color={STATUS_COLOR[a.status] || 'default'} size="small" />
                          </TableCell>
                        )}
                        {visibleColumns.posting && (
                          <TableCell>
                            <Chip
                              label={a.is_header ? 'Header' : a.allow_posting ? 'Allowed' : 'Blocked'}
                              color={!a.is_header && a.allow_posting ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        )}
                        {visibleColumns.flags && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {a.is_control        && <Chip label="Control"   color="warning" size="small" />}
                              {a.is_cash_equivalent && <Chip label="Cash Eq."  color="info"    size="small" />}
                              {a.is_system         && <Chip label="System"    color="default" size="small" />}
                              {a.requires_reconciliation && <Chip label="Recon" size="small" />}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <RowActionsMenu
                            account={a}
                            canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete}
                            onEdit={onEdit} onAddChild={onAddChild} onReparent={onReparent}
                            onActivate={onActivate} onDeactivate={onDeactivate} onDelete={onDelete}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                {visibleTree.length} accounts · page {page} of {totalPages}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                <Button size="small" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};


const CONFIG_ITEMS = [
  { value: 'classes',   label: 'Account Classes' },
  { value: 'types',     label: 'Account Types' },
  { value: 'subtypes',  label: 'Sub-Types' },
  { value: 'templates', label: 'COA Templates' },
];

const ConfigMenu = ({ tab, setTab }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isConfigTab = CONFIG_ITEMS.some((i) => i.value === tab);
  const activeLabel = CONFIG_ITEMS.find((i) => i.value === tab)?.label;

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant={isConfigTab ? 'contained' : 'outlined'}
        endIcon={<ExpandMoreIcon />}
        startIcon={<SettingsIcon />}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        sx={{ mr: 1, mb: '2px', textTransform: 'none', fontWeight: isConfigTab ? 700 : 400 }}
      >
        {isConfigTab ? activeLabel : 'Configuration'}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{ onMouseLeave: () => setOpen(false) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 2, sx: { minWidth: 180, mt: 0.5 } }}
      >
        {CONFIG_ITEMS.map((item) => (
          <MenuItem
            key={item.value}
            selected={tab === item.value}
            onClick={() => { setTab(item.value); setOpen(false); }}
            sx={{ fontSize: '0.875rem' }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const CoaAccounts = () => {
  const { can } = useAccessControl();
  const resource = RESOURCES.LEDGER.COA;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [tab, setTab] = useState('accounts');
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  // Single dialog state for create / edit / child-add
  const [accountDialog, setAccountDialog] = useState({ open: false, mode: 'create', editing: null, parent: null });
  const [accountSaving, setAccountSaving] = useState(false);

  const closeAccountDialog = () => setAccountDialog({ open: false, mode: 'create', editing: null, parent: null });

  // Children viewer + reparent
  const [reparent, setReparent] = useState({ open: false, account: null, parentId: '' });

  // Cascading filter options: classes (for types tab) + types (for subtypes + accounts tabs)
  const [classOptions, setClassOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [subTypeOptions, setSubTypeOptions] = useState([]);
  useEffect(() => {
    ledgerAPI.getAccountClasses({ limit: 100 })
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? {};
        const list = Array.isArray(payload) ? payload : payload.items ?? [];
        setClassOptions(list.map((c) => ({ label: c.class_name || `Class ${c.id}`, value: c.account_class_id ?? c.id })));
      }).catch(() => {});
    ledgerAPI.getAccountTypes({ limit: 100 })
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? {};
        const list = normalizeAccountTypes(Array.isArray(payload) ? payload : payload.items ?? []);
        setTypeOptions(list.map((t) => ({ label: t.type_name || `Type ${t.id}`, value: t.id })));
      }).catch(() => {});
    ledgerAPI.getAccountSubTypes({ limit: 100 })
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? {};
        const list = Array.isArray(payload) ? payload : payload.items ?? [];
        setSubTypeOptions(list.map((s) => ({ label: s.sub_type_name || `Sub-type ${s.id}`, value: s.sub_type_id ?? s.id })));
      }).catch(() => {});
  }, []);

  const runWorkflow = (fn, successMessage) => {
    confirm({
      title: 'Confirm',
      message: 'Are you sure you want to run this action?',
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(successMessage, 'success');
          refresh();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  // ---------- Accounts ----------

  const openAccountAdd   = () => setAccountDialog({ open: true, mode: 'create', editing: null, parent: null });
  const openAccountEdit  = (item) => setAccountDialog({ open: true, mode: 'edit',   editing: normalizeAccount(item), parent: null });
  const openChildAdd     = (parent) => setAccountDialog({ open: true, mode: 'child',  editing: null, parent: normalizeAccount(parent) });

  const submitAccount = async (clean, { reparentTo } = {}) => {
    const { mode, editing, parent } = accountDialog;
    try {
      setAccountSaving(true);
      if (mode === 'edit') {
        await ledgerAPI.updateAccount(editing.id, clean);
        // UpdateAccountDto has no parent_id field — a parent change made in
        // this same dialog goes through the dedicated reparent endpoint
        // right after, so it enforces the same-type/active/no-cycle rules.
        if (reparentTo !== undefined) {
          await ledgerAPI.reparentAccount(editing.id, { parent_id: reparentTo });
        }
        showSnackbar('Account updated successfully', 'success');
      } else if (mode === 'child') {
        await ledgerAPI.createChildAccount({ parent_id: parent.id, ...clean });
        showSnackbar('Child account created successfully', 'success');
      } else {
        await ledgerAPI.createAccount(clean);
        showSnackbar('Account created successfully', 'success');
      }
      closeAccountDialog();
      refresh();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setAccountSaving(false);
    }
  };

  const submitReparent = async () => {
    if (!reparent.account) return;
    try {
      await ledgerAPI.reparentAccount(reparent.account.id, {
        parent_id: reparent.parentId === '' ? null : Number(reparent.parentId),
      });
      showSnackbar('Account moved successfully', 'success');
      setReparent({ open: false, account: null, parentId: '' });
      refresh();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  // ---------- Generic CRUD tab helper ----------

  const CrudTab = ({
    title, subtitle, addLabel, idNormalize, fetchList, createItem, updateItem,
    deleteItem, deleteMessage, columns, fields, initialValues, mapToForm, mapToPayload,
    filters = [], rowActions = [], searchPlaceholder = 'Search...', emptyMessage = 'No records found.',
    pageSize = 50,
  }) => {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState(initialValues);
    const [saving, setSaving] = useState(false);

    const openAdd = () => {
      setEditing(null);
      setValues(initialValues);
      setShowForm(true);
    };
    const openEdit = (item) => {
      const n = idNormalize(item);
      setEditing(n);
      setValues(mapToForm ? mapToForm(n) : n);
      setShowForm(true);
    };
    const handleSubmit = async () => {
      try {
        setSaving(true);
        const payload = mapToPayload ? mapToPayload(values, Boolean(editing)) : values;
        if (editing) {
          await updateItem(editing.id, payload);
          showSnackbar(`${title.slice(0, -1)} updated successfully`, 'success');
        } else {
          await createItem(payload);
          showSnackbar(`${title.slice(0, -1)} created successfully`, 'success');
        }
        setShowForm(false);
        refresh();
      } catch (error) {
        showSnackbar(getErrorMessage(error), 'error');
      } finally {
        setSaving(false);
      }
    };

    return (
      <>
        <LedgerListPage
          resource={resource}
          title={title}
          subtitle={subtitle}
          addLabel={addLabel}
          idKey="id"
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          refreshKey={refreshKey}
          pageSize={pageSize}
          fetchItems={(params) => fetchList(params).then((res) => asListResponse(res, (list) => list.map(idNormalize)))}
          onAdd={canCreate && createItem ? openAdd : null}
          onEdit={canUpdate && updateItem ? openEdit : null}
          onDelete={canDelete && deleteItem ? async (item) => { await deleteItem(idNormalize(item).id); } : null}
          deleteMessage={deleteMessage}
          filters={filters}
          rowActions={rowActions}
          columns={columns}
        />
        {createItem && (
          <LedgerFormDialog
            open={showForm}
            onClose={() => setShowForm(false)}
            title={editing ? `Edit ${title.slice(0, -1)}` : addLabel}
            fields={fields}
            values={values}
            onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
            onSubmit={handleSubmit}
            saving={saving}
          />
        )}
      </>
    );
  };

  const stateAction = (kind, id, verb, label, pastTense, color = 'default') => ({
    icon: verb === 'lock' ? LockIcon : verb === 'unlock' ? UnlockIcon : verb === 'activate' ? ActivateIcon : DeactivateIcon,
    title: label,
    color,
    onClick: () =>
      runWorkflow(
        () =>
          ({
            lockType: ledgerAPI.lockAccountType,
            unlockType: ledgerAPI.unlockAccountType,
            activateType: ledgerAPI.activateAccountType,
            deactivateType: ledgerAPI.deactivateAccountType,
            lockSub: ledgerAPI.lockAccountSubType,
            unlockSub: ledgerAPI.unlockAccountSubType,
            activateSub: ledgerAPI.activateAccountSubType,
            deactivateSub: ledgerAPI.deactivateAccountSubType,
          }[kind](id)),
        `${label} ${pastTense}`
      ),
  });

  return (
    <Box>
      <ModulePageHeader
        title="Chart of Accounts"
        subtitle="Classes own code fences, types divide them, sub-types imply ranges, accounts hold postings"
      />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs value={tab === 'accounts' ? 'accounts' : false} onChange={(_, v) => v && setTab(v)}>
              <Tab value="accounts" label="Accounts" />
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {tab === 'accounts' && canCreate && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAccountAdd}>Add Account</Button>
              )}
              <ConfigMenu tab={tab} setTab={setTab} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {tab === 'accounts' && (
        <>
          <AccountsTreeTable
            refreshKey={refreshKey}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onEdit={openAccountEdit}
            onAddChild={openChildAdd}
            onReparent={(a) => setReparent({ open: true, account: a, parentId: a.parent_id ?? '' })}
            onActivate={(a) => runWorkflow(() => ledgerAPI.activateAccount(a.id), 'Account activated')}
            onDeactivate={(a) => runWorkflow(() => ledgerAPI.deactivateAccount(a.id), 'Account deactivated')}
            onDelete={(a) => {
              confirm({
                title: 'Delete Account',
                message: `Delete ${a.account_code} - ${a.account_name}? System, control, parent and posted accounts cannot be deleted.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteAccount(a.id);
                    showSnackbar('Account deleted', 'success');
                    refresh();
                  } catch (e) { showSnackbar(getErrorMessage(e), 'error'); }
                },
              });
            }}
          />
          <AccountDialog
            open={accountDialog.open}
            onClose={closeAccountDialog}
            onSubmit={submitAccount}
            saving={accountSaving}
            mode={accountDialog.mode}
            editing={accountDialog.editing}
            parentAccount={accountDialog.parent}
          />
          <Dialog open={reparent.open} onClose={() => setReparent({ open: false, account: null, parentId: '' })} maxWidth="xs" fullWidth>
            <DialogTitle>Move {reparent.account?.account_code}</DialogTitle>
            <DialogContent>
              <AccountPicker
                label="New parent account (clear = top level)"
                value={reparent.parentId}
                onChange={(id) => setReparent((p) => ({ ...p, parentId: id }))}
                exclude={reparent.account?.id}
                typeId={reparent.account?.sub_type?.accountType?.account_type_id ?? reparent.account?.account_type_id}
                activeOnly
                helper="Only active accounts of the same type are shown."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReparent({ open: false, account: null, parentId: '' })}>Cancel</Button>
              <Button variant="contained" onClick={submitReparent}>Move</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tab === 'classes' && (
        <CrudTab
          title="Account Classes"
          subtitle="Top of the hierarchy - each class owns a code fence (no overlaps)"
          addLabel="Add Class"
          idNormalize={normalizeAccountClass}
          fetchList={(p) => ledgerAPI.getAccountClasses(p)}
          createItem={(d) => ledgerAPI.createAccountClass(d)}
          updateItem={(id, d) => ledgerAPI.updateAccountClass(id, d)}
          deleteItem={(id) => ledgerAPI.deleteAccountClass(id)}
          deleteMessage={(c) => `Delete class ${c.class_name}? System classes and classes with types cannot be deleted.`}
          searchPlaceholder="Search classes..."
          emptyMessage="No account classes found."
          filters={[
            { key: 'reporting_category',      label: 'Category',   options: REPORTING_CATEGORY_OPTS },
            { key: 'financial_statement_type', label: 'Statement',  options: FINANCIAL_STATEMENT_OPTS },
            { key: 'balance_type',            label: 'Balance',    options: BALANCE_TYPE_OPTS },
            { key: 'is_active',               label: 'Active',     options: boolOpts('All') },
            { key: 'system_defined',          label: 'System',     options: boolOpts('All') },
          ]}
          columns={[
            { label: 'Name', render: (c) => <strong>{c.class_name}</strong> },
            { label: 'Range', render: (c) => `${c.code_start} - ${c.code_end}` },
            { label: 'Balance', render: (c) => c.balance_type },
            { label: 'Statement', render: (c) => c.financial_statement_type },
            { label: 'Status', render: (c) => activeChip(c.is_active) },
          ]}
          fields={CLASS_FIELDS}
          initialValues={{ class_name: '', code_start: '', code_end: '', balance_type: 'debit', financial_statement_type: 'balance_sheet', reporting_category: 'asset', cash_flow_type: 'operating' }}
          mapToForm={(c) => ({
            class_name: c.class_name || '', code_start: c.code_start ?? '', code_end: c.code_end ?? '',
            balance_type: c.balance_type || 'debit', financial_statement_type: c.financial_statement_type || 'balance_sheet',
            reporting_category: c.reporting_category || 'asset', cash_flow_type: c.cash_flow_type || 'operating',
          })}
          mapToPayload={(v) => ({ ...v, code_start: toNum(v.code_start), code_end: toNum(v.code_end) })}
        />
      )}

      {tab === 'types' && (
        <CrudTab
          title="Account Types"
          subtitle="Divide a class fence - locked/system types cannot change"
          addLabel="Add Type"
          idNormalize={normalizeAccountType}
          fetchList={(p) => ledgerAPI.getAccountTypes(p)}
          createItem={(d) => ledgerAPI.createAccountType(d)}
          updateItem={(id, d) => ledgerAPI.updateAccountType(id, d)}
          deleteItem={(id) => ledgerAPI.deleteAccountType(id)}
          deleteMessage={(t) => `Delete type ${t.type_name}? Locked types and types with sub-types cannot be deleted.`}
          searchPlaceholder="Search types..."
          emptyMessage="No account types found."
          filters={[
            { key: 'account_class_id',    label: 'Class',   options: [{ label: 'All classes', value: '' }, ...classOptions] },
            { key: 'balance_sheet_section', label: 'BS Section', options: BALANCE_SHEET_SECTION_OPTS },
            { key: 'is_active',           label: 'Active',  options: boolOpts('All') },
            { key: 'system_defined',      label: 'System',  options: boolOpts('All') },
          ]}
          columns={[
            { label: 'Name', render: (t) => <strong>{t.type_name}</strong> },
            { label: 'Code', render: (t) => t.code || '-' },
            { label: 'Class', render: (t) => t.accountClass?.class_name || t.account_class_id || '-' },
            { label: 'Range', render: (t) => (t.suggested_range_start ? `${t.suggested_range_start} - ${t.suggested_range_end}` : 'auto') },
            { label: 'Status', render: (t) => activeChip(t.is_active) },
          ]}
          fields={TYPE_FIELDS}
          initialValues={{ type_name: '', account_class_id: '', code: '', description: '', suggested_range_start: '', suggested_range_end: '', balance_sheet_section: '', display_order: 0, is_active: true }}
          mapToForm={(t) => ({
            type_name: t.type_name || '', account_class_id: t.account_class_id ?? '', code: t.code || '',
            description: t.description || '', suggested_range_start: t.suggested_range_start ?? '',
            suggested_range_end: t.suggested_range_end ?? '', balance_sheet_section: t.balance_sheet_section || '',
            display_order: t.display_order ?? 0, is_active: t.is_active !== false,
          })}
          mapToPayload={(v) => {
            const p = { ...v };
            ['account_class_id', 'suggested_range_start', 'suggested_range_end', 'display_order'].forEach((k) => {
              p[k] = toNum(p[k]);
              if (p[k] === undefined) delete p[k];
            });
            if (!p.code) delete p.code;
            if (!p.balance_sheet_section) delete p.balance_sheet_section;
            if (!p.description) delete p.description;
            return p;
          }}
          rowActions={(t) => {
            const n = normalizeAccountType(t);
            const acts = [];
            if (canUpdate) {
              acts.push(n.locked_at
                ? stateAction('unlockType', n.id, 'unlock', 'Unlock', 'unlocked')
                : stateAction('lockType', n.id, 'lock', 'Lock', 'locked', 'warning'));
              acts.push(n.is_active
                ? stateAction('deactivateType', n.id, 'deactivate', 'Deactivate', 'deactivated', 'warning')
                : stateAction('activateType', n.id, 'activate', 'Activate', 'activated', 'success'));
            }
            return acts;
          }}
        />
      )}

      {tab === 'subtypes' && (
        <CrudTab
          title="Account Sub-Types"
          subtitle="Imply account code ranges - control accounts force reconciliation and block posting"
          addLabel="Add Sub-Type"
          idNormalize={normalizeSubType}
          fetchList={(p) => ledgerAPI.getAccountSubTypes(p)}
          createItem={(d) => ledgerAPI.createAccountSubType(d)}
          updateItem={(id, d) => ledgerAPI.updateAccountSubType(id, d)}
          deleteItem={(id) => ledgerAPI.deleteAccountSubType(id)}
          deleteMessage={(s) => `Delete sub-type ${s.sub_type_name}? Sub-types with accounts cannot be deleted.`}
          searchPlaceholder="Search sub-types..."
          emptyMessage="No account sub-types found."
          filters={[
            { key: 'account_type_id',    label: 'Type',          options: [{ label: 'All types', value: '' }, ...typeOptions] },
            { key: 'is_active',          label: 'Active',        options: boolOpts('All') },
            { key: 'is_control_account', label: 'Control',       options: boolOpts('All') },
            { key: 'is_posting_allowed', label: 'Posting',       options: boolOpts('All') },
            { key: 'system_defined',     label: 'System',        options: boolOpts('All') },
          ]}
          columns={[
            { label: 'Name', render: (s) => <strong>{s.sub_type_name}</strong> },
            { label: 'Code', render: (s) => s.code || '-' },
            { label: 'Control', render: (s) => (s.is_control_account ? <Chip label="Control" color="warning" size="small" /> : '-') },
            { label: 'Posting', render: (s) => (s.is_posting_allowed === false ? <Chip label="Blocked" size="small" /> : <Chip label="Allowed" color="success" size="small" />) },
            { label: 'Status', render: (s) => activeChip(s.is_active) },
          ]}
          fields={SUBTYPE_FIELDS}
          initialValues={{ sub_type_name: '', account_type_id: '', code: '', description: '', suggested_range_start: '', suggested_range_end: '', is_control_account: false, requires_reconciliation: false, is_posting_allowed: true, internal_code: '', display_order: 0, is_active: true }}
          mapToForm={(s) => ({
            sub_type_name: s.sub_type_name || '', account_type_id: s.account_type_id ?? '', code: s.code || '',
            description: s.description || '', suggested_range_start: s.suggested_range_start ?? '',
            suggested_range_end: s.suggested_range_end ?? '', is_control_account: Boolean(s.is_control_account),
            requires_reconciliation: Boolean(s.requires_reconciliation), is_posting_allowed: s.is_posting_allowed !== false,
            internal_code: s.internal_code || '', display_order: s.display_order ?? 0, is_active: s.is_active !== false,
          })}
          mapToPayload={(v) => {
            const p = { ...v };
            ['account_type_id', 'suggested_range_start', 'suggested_range_end', 'display_order'].forEach((k) => {
              p[k] = toNum(p[k]);
              if (p[k] === undefined) delete p[k];
            });
            ['code', 'description', 'internal_code'].forEach((k) => { if (!p[k]) delete p[k]; });
            return p;
          }}
          rowActions={(s) => {
            const n = normalizeSubType(s);
            const acts = [];
            if (canUpdate) {
              acts.push(n.locked_at
                ? stateAction('unlockSub', n.id, 'unlock', 'Unlock', 'unlocked')
                : stateAction('lockSub', n.id, 'lock', 'Lock', 'locked', 'warning'));
              acts.push(n.is_active
                ? stateAction('deactivateSub', n.id, 'deactivate', 'Deactivate', 'deactivated', 'warning')
                : stateAction('activateSub', n.id, 'activate', 'Activate', 'activated', 'success'));
            }
            return acts;
          }}
        />
      )}

      {tab === 'templates' && (
        <>
          <LedgerListPage
            resource={resource}
            title="COA Templates"
            subtitle="Read-only seed charts for onboarding new entities"
            idKey="id"
            searchPlaceholder="Search templates..."
            emptyMessage="No COA templates found."
            pageSize={50}
            refreshKey={refreshKey}
            fetchItems={(params) => ledgerAPI.getCoaTemplates(params).then((res) => asListResponse(res, (list) => list.map((t) => ({ ...t, id: t.template_id ?? t.id }))))}
            columns={[
              { label: 'Code', render: (t) => <strong>{t.template_code}</strong> },
              { label: 'Name', render: (t) => t.name },
              { label: 'Industry', render: (t) => t.industry || '-' },
              { label: 'Version', render: (t) => t.version || '-' },
              { label: 'Status', render: (t) => activeChip(t.is_active) },
            ]}
          />
          {!can(resource, ACTIONS.READ) && <Alert severity="error">You do not have permission to view the chart of accounts.</Alert>}
        </>
      )}

      {SnackbarComponent}
      {ConfirmDialogComponent}
    </Box>
  );
};

export default CoaAccounts;
